import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { EmptyState } from "@/components/Loaders";
import { Shield, Users, Lightbulb, FolderKanban, FileText, Rocket, Check, X, Mail } from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [subscribers, setSubscribers] = useState([]);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/submissions").then((r) => setSubmissions(r.data));
    api.get("/admin/users").then((r) => setUsers(r.data));
    api.get("/admin/newsletter").then((r) => setSubscribers(r.data));
  };
  useEffect(() => { load(); }, []);

  const moderate = async (slug, action) => {
    await api.post(`/admin/submissions/${slug}/${action}`);
    toast.success(`Submission ${action}d`);
    load();
  };

  if (!stats) return null;

  const cards = [
    { icon: Users, label: "Total Users", value: stats.users },
    { icon: Lightbulb, label: "Ideas Validated", value: stats.ideas_validated },
    { icon: FolderKanban, label: "Projects", value: stats.projects },
    { icon: FileText, label: "AI Reports", value: stats.ai_reports },
    { icon: Rocket, label: "Launches", value: stats.posts },
    { icon: Shield, label: "Pending Subs", value: stats.pending_submissions },
    { icon: Mail, label: "Subscribers", value: stats.newsletter },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="font-display text-3xl font-extrabold text-slate-50 flex items-center gap-3 mb-8"><Shield className="w-7 h-7 text-cyan-400" />Admin Dashboard</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} className="glass rounded-xl p-5" data-testid={`admin-stat-${c.label.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
            <c.icon className="w-5 h-5 text-cyan-400 mb-2" />
            <p className="font-mono text-2xl font-bold text-slate-50">{c.value}</p>
            <p className="text-xs text-slate-500">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="glass rounded-xl p-5 mb-8">
        <h3 className="font-semibold text-slate-200 mb-2">Platform</h3>
        <p className="text-sm text-slate-500">NexxVibe runs as a free platform — no subscriptions or billing to manage.</p>
      </div>

      <Tabs defaultValue="submissions">
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger value="submissions" data-testid="admin-tab-submissions">Submissions ({submissions.length})</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Users ({users.length})</TabsTrigger>
          <TabsTrigger value="newsletter" data-testid="admin-tab-newsletter">Subscribers ({subscribers.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="submissions" className="mt-6">
          {submissions.length === 0 ? <EmptyState icon={Check} title="No pending submissions" subtitle="Community submissions will appear here for moderation." /> : (
            <div className="space-y-3">
              {submissions.map((s) => (
                <div key={s.slug} className="glass rounded-xl p-5" data-testid={`submission-${s.slug}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h4 className="font-semibold text-slate-100">{s.title}</h4>
                      <p className="text-sm text-slate-400 mt-1">{s.problem}</p>
                      <p className="text-xs text-slate-500 mt-2">Target: {s.target_customer}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" onClick={() => moderate(s.slug, "approve")} className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" data-testid={`approve-${s.slug}`}><Check className="w-4 h-4" /></Button>
                      <Button size="sm" onClick={() => moderate(s.slug, "reject")} className="bg-red-500/20 text-red-300 border border-red-500/30" data-testid={`reject-${s.slug}`}><X className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <div className="glass rounded-xl overflow-hidden">
            {users.map((u) => (
              <div key={u.user_id} className="flex items-center gap-3 p-4 border-b border-white/5 last:border-0">
                <Avatar className="w-8 h-8"><AvatarImage src={u.picture} /><AvatarFallback className="bg-cyan-500/20 text-cyan-300 text-xs">{u.name?.[0]}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0"><p className="text-sm text-slate-200 truncate">{u.name}</p><p className="text-xs text-slate-500 truncate">{u.email}</p></div>
                <span className="text-xs font-mono uppercase text-cyan-400">{u.plan}</span>
                {u.role === "admin" && <span className="text-[10px] font-mono uppercase text-amber-400">admin</span>}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="newsletter" className="mt-6">
          {subscribers.length === 0 ? <EmptyState icon={Mail} title="No subscribers yet" subtitle="Waitlist & newsletter signups will appear here." /> : (
            <div className="glass rounded-xl overflow-hidden">
              {subscribers.map((s) => (
                <div key={s.email} className="flex items-center gap-3 p-4 border-b border-white/5 last:border-0" data-testid={`subscriber-${s.email}`}>
                  <Mail className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span className="text-sm text-slate-200 flex-1 min-w-0 truncate">{s.email}</span>
                  <span className="text-[10px] font-mono uppercase text-slate-500">{s.source}</span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
